import React, { useState } from "react";
import { AutoFixHigh, HighlightAlt, HubOutlined } from "@mui/icons-material";
import { Alert, Box, Typography } from "@mui/material";

import { NavigationButton } from "ui";

import { selectVersionsByProjectId, useGetVersionConfigQuery } from "fond/api";
import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import CreateScenarioModal from "fond/project/versions/CreateScenarioModal";
import { useTopologicalViewContext } from "fond/topologicalView/TopologicalViewContext";
import { WorkflowKey } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";
import { BlockSpinner } from "fond/widgets";

import DataModificationPanel from "./DataModificationPanel";

type ScenarioTarget = "excludeDraw" | "optimize";

const scenarioDescriptions: Record<ScenarioTarget, string> = {
  excludeDraw: "This will create a new scenario version based on the original, with your excluded addresses applied.",
  optimize: "This will create a new scenario version based on the original, optimized for your financial objectives.",
};

const BASE_DESIGN_REQUIRED_MESSAGE = "Run cost to serve before creating scenarios.";

interface AnalysisPanelProps {
  isDisabled?: boolean;
  workflowKey: WorkflowKey;
  /**
   * Workflow key for the Optimize scenario. The Optimize option is shown when provided.
   */
  optimizeWorkflowKey?: WorkflowKey;
  /**
   * Whether a base cost-to-serve design has already been generated for this version.
   */
  isBaseDesignAvailable?: boolean;
}

const AnalysisPanel: React.FC<AnalysisPanelProps> = ({ isDisabled, workflowKey, optimizeWorkflowKey, isBaseDesignAvailable = false }) => {
  const projectId = useAppSelector((state) => state.project.projectId);
  const versionId = useAppSelector((state) => state.project.versionId);
  const versions = useAppSelector((state) => selectVersionsByProjectId(state, projectId));
  const { isLoading } = useGetVersionConfigQuery(versionId);
  const [scenarioTarget, setScenarioTarget] = useState<ScenarioTarget | null>(null);

  const { workflowState } = useCustomWorkflow({ versionId, key: workflowKey });
  const { workflowState: optimizeWorkflowState } = useCustomWorkflow({
    versionId,
    key: optimizeWorkflowKey,
  });
  const isGeneratingCTS = [workflowState, optimizeWorkflowState].some((state) => state === "STARTING" || state === "IN_PROGRESS");

  const { isAvailable: isTopologicalViewAvailable, openView: openTopologicalView } = useTopologicalViewContext();

  if (isLoading) {
    return <BlockSpinner />;
  }

  return (
    <>
      {isTopologicalViewAvailable && (
        <Box pb={2}>
          <NavigationButton
            data-testid="open-topological-view-navigation"
            icon={<HubOutlined />}
            title="Splicing view"
            subtitle="Review the topological splice diagram."
            onClick={openTopologicalView}
            sx={{ m: 0 }}
          />
        </Box>
      )}

      {!isGeneratingCTS && (
        <>
          <Typography fontWeight="500">New Scenarios</Typography>
          <Box py={1}>
            <Typography variant="caption">Adjust your build scope to see the updated cost to serve analysis.</Typography>
          </Box>
          {!isBaseDesignAvailable && (
            <Alert severity="info" variant="compact" sx={{ mb: 1 }} data-testid="base-design-required-alert">
              {BASE_DESIGN_REQUIRED_MESSAGE}
            </Alert>
          )}
          <NavigationButton
            data-testid="select-scenario-navigation"
            icon={<HighlightAlt />}
            title="Select"
            subtitle="Defer based on manual selection or cost."
            onClick={() => setScenarioTarget("excludeDraw")}
            sx={{ m: 0, mt: 1 }}
            disabled={isDisabled || isGeneratingCTS || !isBaseDesignAvailable}
          />
          {optimizeWorkflowKey && (
            <NavigationButton
              data-testid="optimize-scenario-navigation"
              icon={<AutoFixHigh />}
              title="Optimize"
              subtitle="Defer based on your financial objectives."
              onClick={() => setScenarioTarget("optimize")}
              sx={{ m: 0, mt: 1 }}
              disabled={isDisabled || isGeneratingCTS || !isBaseDesignAvailable}
            />
          )}
        </>
      )}

      <Box my={1}>
        <DataModificationPanel workflowKey={workflowKey} isDisabled={isDisabled} />
        {optimizeWorkflowKey && <DataModificationPanel workflowKey={optimizeWorkflowKey} isDisabled={isDisabled} />}
      </Box>

      {scenarioTarget && (
        <CreateScenarioModal
          target={scenarioTarget}
          description={scenarioDescriptions[scenarioTarget]}
          onClose={() => {
            setScenarioTarget(null);
          }}
          versionNames={versions?.map((version) => version.Name)}
        />
      )}
    </>
  );
};

export default AnalysisPanel;
